import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  FlowUpstreamVirtualConnectionsFilteredByFlowOutputFieldQuery,
  FlowUpstreamVirtualConnectionsFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_upstreamVirtualConnectionsConnection_nodes,
  FlowUpstreamVirtualConnectionsFilteredByFlowOutputFieldQueryVariables,
} from '../../../gen-src/queries/FlowUpstreamVirtualConnectionsFilteredByFlowOutputFieldQuery';
import {
  FlowUpstreamVirtualConnectionsQuery,
  FlowUpstreamVirtualConnectionsQuery_flows_upstreamVirtualConnectionsConnection_nodes,
  FlowUpstreamVirtualConnectionsQueryVariables,
} from '../../../gen-src/queries/FlowUpstreamVirtualConnectionsQuery';
import {
  VirtualConnectionsDisplayGridRowDataFragment,
} from '../../../gen-src/queries/VirtualConnectionsDisplayGridRowDataFragment';
import { VirtualConnectionSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  FlowUpstreamVirtualConnectionsFilteredByFlowOutputFieldQueryGql,
} from '../../queries/FlowUpstreamVirtualConnectionsFilteredByFlowOutputFieldQueryGql';
import { FlowUpstreamVirtualConnectionsQueryGql } from '../../queries/FlowUpstreamVirtualConnectionsQueryGql';
import { getRowDataWithMaxCountsForVirtualConnection } from '../database/functions/VirtualConnectionRowDataFunctions';
import { VirtualConnectionsDisplayGridRowData } from '../database/types/VirtualConnectionDisplayGridRowData';
import { VirtualConnectionsDisplayGrid } from '../sharedDisplayGrid/VirtualConnectionsDisplayGrid';
import {
  VirtualConnectionsValidSortFields,
} from '../sorting/sortProviders/VirtualConnectionsGridSortProvider';

type QueryVariables = FlowUpstreamVirtualConnectionsQueryVariables;
type QueryResults = FlowUpstreamVirtualConnectionsQuery;
type QueryResultListNode = FlowUpstreamVirtualConnectionsQuery_flows_upstreamVirtualConnectionsConnection_nodes;
type GridRowData = VirtualConnectionsDisplayGridRowData;

type QueryVariablesWithFilter = FlowUpstreamVirtualConnectionsFilteredByFlowOutputFieldQueryVariables;
type QueryResultsWithFilter = FlowUpstreamVirtualConnectionsFilteredByFlowOutputFieldQuery;
type QueryResultListNodeWithFilter =
  FlowUpstreamVirtualConnectionsFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_upstreamVirtualConnectionsConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.upstreamVirtualConnectionsConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.upstreamVirtualConnectionsConnection : null;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow;
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field;
}

function getRowDataForVirtualConnection(
  table: VirtualConnectionsDisplayGridRowDataFragment,
  resultData: QueryResults,
): VirtualConnectionsDisplayGridRowData {
  return getRowDataWithMaxCountsForVirtualConnection(table, selectMaxCountsResult(resultData));
}

function getRowDataForVirtualConnectionForFilteredQuery(
  table: VirtualConnectionsDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): VirtualConnectionsDisplayGridRowData {
  return getRowDataWithMaxCountsForVirtualConnection(table, selectMaxCountsResultForFilteredQuery(resultData));
}

export type FlowUpstreamVirtualConnectionsQueryGridProps =
  GridSortProps<VirtualConnectionsValidSortFields> & {
    readonly flowVizportalUrlId: string;
    readonly filteredFlowOutputFieldId: string | null;
    readonly rowCount: number | null;
  };

export function FlowUpstreamVirtualConnectionsQueryGrid(props: FlowUpstreamVirtualConnectionsQueryGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  const emptyGridMessage = Messages.EmptyUpstreamVirtualConnectionsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredVirtualConnectionsLineageGridMessage();

  return props.filteredFlowOutputFieldId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={FlowUpstreamVirtualConnectionsFilteredByFlowOutputFieldQueryGql}
      partialQueryVariables={{
        flowVizportalUrlId: props.flowVizportalUrlId,
        selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
        orderBy: props.orderBy as VirtualConnectionSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForVirtualConnectionForFilteredQuery}
    >
      {({ rowStore }) =>
        <VirtualConnectionsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={FlowUpstreamVirtualConnectionsQueryGql}
      partialQueryVariables={{
        flowVizportalUrlId: props.flowVizportalUrlId,
        orderBy: props.orderBy as VirtualConnectionSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForVirtualConnection}
    >
      {({ rowStore }) =>
        <VirtualConnectionsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  );
}
